import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useMe from '@app/hooks/auth/useMe';
import useNumberActions from '@app/hooks/numbers/useNumberActions';
import useNumbers from '@app/hooks/numbers/useNumbers';
import useModal from '@app/hooks/useModal';
import type { AINumber, AINumberListParams } from '@app/services/api.types';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiLink, HiOutlineTrash } from 'react-icons/hi2'; //HiOutlinePencil,
import { MdAdd } from 'react-icons/md';
import { Link } from 'react-router';
import { AssignAiAgentModal } from './components/AssignAiAgentModal';
import { NumberAddModal } from './components/NumberAddModal';
import { NumberDeleteModal } from './components/NumberDeleteModal';
import NumberDrawer from './components/NumberDrawer';
import NumberFilters from './components/NumberFilters';

export const AiNumbersList = () => {
    const { exportNumbers } = useNumberActions();
    const [selectedRecord, setSelectedRecord] = useState<AINumber | null>(null);
    const [params, setParams] = useState<AINumberListParams>({
        page: 1,
        per_page: 10,
        search: '',
        order_by: undefined,
        order_dir: undefined,
    });
    const { data: me } = useMe();

    const modalAdd = useModal<AINumber>({
        initialData: undefined,
    });

    const modalEdit = useModal<AINumber>({
        initialData: undefined,
    });

    const modalRemove = useModal<AINumber>({
        initialData: undefined,
    });

    const { data, isLoading } = useNumbers(params);

    const columns: TableColumnsType<AINumber> = [
        {
            title: 'Agent Number',
            dataIndex: 'number',
            key: 'number',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Agent Name',
            dataIndex: 'agent_name',
            key: 'agent_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Agent Type',
            dataIndex: 'agent_type',
            key: 'agent_type',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            render: (value, record) => {
                const brand = record.brand;
                if (brand && value) return `${brand}  ${value}`;
                return value || brand || '-';
            },
            sorter: false,
        },
        {
            title: 'Failover Number',
            dataIndex: 'failover_number',
            key: 'number',
            render: (value) => value || '-',
            sorter: false,
        },
        // {
        //     title: 'Created At',
        //     dataIndex: 'created_at',
        //     key: 'created_at',
        //     render: (value) => new Date(value).toLocaleDateString() || '-',
        //     sorter: false,
        // },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: true,
            width: 150,
            fixed: 'right',
            hidden: !me?.is_internal,
            render: (_value, record) => (
                <StyledButtonGroup justify="flex-start">
                    <StyledButton
                        icon={<HiLink />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalEdit.open(record);
                        }}>
                        Assign
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        size="small"
                        color="red"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalRemove.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    // const openEditModal = (record: AINumber) => {
    //     modalEdit.open(record);
    // };

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeAddModal = () => {
        modalAdd.reset();
    };

    const closeRemoveModal = () => {
        modalRemove.reset();
    };

    const handleExport = async () => {
        exportNumbers.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="AI Numbers" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'AI Agents' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="AI Numbers" />
                        <PageIntro>
                            <p>Manage AI agent numbers</p>
                        </PageIntro>
                    </div>
                    {me?.is_internal ? (
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    color="blue"
                                    icon={<MdAdd size={20} />}
                                    onClick={() => modalAdd.open()}>
                                    Add New
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    ) : null}
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <NumberFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    disabled={Boolean(!data?.total)}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<AINumber>
                        columns={columns}
                        dataSource={data?.data ?? []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: data?.total ?? 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <NumberDrawer
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <NumberAddModal
                open={modalAdd.isOpen}
                onCancel={closeAddModal}
                onOk={closeAddModal}
                title="New AI Agent Number"
            />

            <AssignAiAgentModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <NumberDeleteModal
                open={modalRemove.isOpen}
                data={modalRemove.data}
                onCancel={closeRemoveModal}
                onOk={closeRemoveModal}
            />
        </React.Fragment>
    );
};

export default AiNumbersList;
